import React, {useEffect, useMemo, useState} from 'react';
import {Box, Dialog, DialogActions, DialogContent, DialogTitle, makeStyles} from '@material-ui/core';
import {IconClose, IconHeart} from '../../../components/Icons';
import {rieltor} from '../../../styles/theme';
import Typo from '../../../components/Typo';
import {ButtonGray, ButtonPurple} from '../../../components/Buttons';
import {useCurrentUser} from '../../../hooks/user';
import {UsersAPI} from '../../../services/UsersAPI';
import {fetchUserInfo} from '../../../store/user/actions';
import {useDispatch, useSelector} from 'react-redux';
import {APP_REVIEW_DIALOG, getDialogName} from '../../../store/offers/reducers/actionDialogs';
import {FeedbackType} from '../../../components/FeedbackForm/types';
import {FeedbackFormDialog} from '../../../components/FeedbackForm';
import {useIsAndroidRieltorApp, useIsRieltorApp} from '../../../hooks/useIsRieltorApp';
import {Star} from '@material-ui/icons';
import {hasIosAppReviewCapability, requestIosAppReview} from '../../../common/ios';
import ReactGA from 'react-ga';
import {androidAppReviewSelector, iosAppReviewSelector} from '../../../store/global/selectors';
import {hasAndroidAppReviewCapability, requestAndroidAppReview} from '../../../common/android';
import {IsAlertVisible} from '../../../utils';

const ALERT_ID_IOS_APP_REVIEW = '420';
const ALERT_ID_ANDROID_APP_REVIEW = '440';
const useStyles = makeStyles((theme) => ({
  popupWrapper: {
    '& .MuiDialog-paper': {
      maxWidth: '650px !important',
      margin: '5px',
    },
    '& .MuiDialogContent-root': {
      padding: '0px 16px 0',
    },
    '& button': {
      borderRadius: '8px',
    },
  },
  actionButton: {
    width: '165px',
    height: '59px',
    fontSize: '16px',
    fontWeight: 700,
    padding: '0',

    '&:first-child': {
      marginRight: '20px',
    },
  },
  stars: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
  },
  star: {
    width: 32,
    height: 32,
  },
  starButton: {
    width: '80%',
    height: '40px',
    margin: '20px 10% 44px 10%',
  },
  title1: {
    lineHeight: '30px',
  },
  title2: {
    lineHeight: '30px',
  },
}));
const RieltorAppReview = () => {
  const classes = useStyles();
  const usersAPI = useMemo(() => new UsersAPI(), []);
  const dispatch = useDispatch();
  const dialogName = useSelector(getDialogName);
  const isIosApp = useIsRieltorApp();
  const isAndroidRieltorApp = useIsAndroidRieltorApp();
  const iosAppReview = useSelector(iosAppReviewSelector);
  const androidAppReview = useSelector(androidAppReviewSelector);

  const appReviewConfig = isIosApp ? iosAppReview : isAndroidRieltorApp ? androidAppReview : null;
  const feedbackType = isIosApp ? FeedbackType.IosAppReview : isAndroidRieltorApp ? FeedbackType.AndroidAppReview : FeedbackType.AndroidAppReview;

  const user = useCurrentUser();
  const [open, setOpen] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [isLikeOpened, setIsLikeOpened] = useState(false);

  const alertId = isIosApp ? ALERT_ID_IOS_APP_REVIEW : isAndroidRieltorApp ? ALERT_ID_ANDROID_APP_REVIEW : ALERT_ID_ANDROID_APP_REVIEW;
  const isAlertVisible = IsAlertVisible(user, alertId, null);
  const isDialogOpen = dialogName === APP_REVIEW_DIALOG;

  const isApp = isAndroidRieltorApp && hasAndroidAppReviewCapability || isIosApp && hasIosAppReviewCapability();

  useEffect(() => {
    if (!isApp || !isAlertVisible || !isDialogOpen) {
      return;
    }
    ReactGA.event({
      category: 'app_review',
      action: 'show',
    });
    setOpen(true);
    try {
      alertCloseTemporarily();
    } catch (e) {
    }
  }, [isAlertVisible, isIosApp, isDialogOpen]);

  const alertClosePermanently = async () => {
    try {
      await usersAPI.updateAlertStatus({id: alertId, isManuallyClosed: true});
      dispatch(fetchUserInfo());
    } catch (e) {
      console.error('Failed to close alert permanently', e);
    }
  }

  const alertCloseTemporarily = async () => {
    try {
      await usersAPI.updateAlertStatus({id: alertId, firstView: true});
      dispatch(fetchUserInfo());
    } catch (e) {
      console.error('Failed to close alert temporarily', e);
    }
  }

  const handleLikeClick = () => {
    setOpen(false);
    setIsLikeOpened(true);

    ReactGA.event({
      category: 'app_review',
      action: 'like_click',
    });
  };

  const handleDislikeClick = () => {
    setOpen(false);
    setShowFeedback(true);
    alertCloseTemporarily();


    ReactGA.event({
      category: 'app_review',
      action: 'dislike_click',
    });
  };

  const handleReviewClick = () => {
    setIsLikeOpened(false);

    ReactGA.event({
      category: 'app_review',
      action: 'review_click',
    });

    alertClosePermanently();
  };

  const handleLikeClose = () => {
    setIsLikeOpened(false);

    ReactGA.event({
      category: 'app_review',
      action: 'like_close',
    });

    alertCloseTemporarily();

  };

  const handleReviewClose = () => {
    setOpen(false);

    ReactGA.event({
      category: 'app_review',
      action: 'review_close',
    });

    alertCloseTemporarily();
  };

  if (user === null) {
    return null;
  }

  return (
      <>
        <Dialog
            className={classes.popupWrapper}
            open={open}
        >
          <DialogTitle style={{padding: '8px 16px'}}>
            <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
              <div>&nbsp;</div>

              <IconClose
                  style={{cursor: 'pointer', color: rieltor.gray4}}
                  onClick={handleReviewClose}
              />
            </Box>
          </DialogTitle>

          <DialogContent>
            <Typo variant={'title1'} mb={4} className={classes.title1}>
              {appReviewConfig?.mainTitle || 'Як вам наш додаток Rieltor від ЛУН?'}
            </Typo>
          </DialogContent>

          <DialogActions style={{justifyContent: 'center', padding: '0 16px 20px 16px'}}>
            <ButtonGray onClick={handleDislikeClick} size={'large'} className={classes.actionButton}>
              Не подобається
            </ButtonGray>
            <ButtonPurple onClick={handleLikeClick} size={'large'} className={classes.actionButton}>
              Подобається
            </ButtonPurple>
          </DialogActions>
        </Dialog>
        {showFeedback &&
          <FeedbackFormDialog
            type={feedbackType}
            titleIcon={<IconHeart/>}
            isOpened={showFeedback}
            minLength={5}
            onClose={() => setShowFeedback(false)}
            question={appReviewConfig?.feedbackText || 'Нам дуже важливий ваш відгук. Що ми можемо зробити краще?'}
            title={'Залишити відгук'}
          />
        }
        {isLikeOpened && <RieltorAppReviewLike onLike={handleReviewClick} onClose={handleLikeClose}/>}
      </>
  );
};

export default RieltorAppReview;

const RieltorAppReviewLike = ({onLike, onClose}: { onLike: () => void, onClose: () => void }) => {
  const classes = useStyles();
  const iosAppReview = useSelector(iosAppReviewSelector);
  const androidAppReview = useSelector(androidAppReviewSelector);
  const isIosApp = useIsRieltorApp();
  const isAndroidRieltorApp = useIsAndroidRieltorApp();

  const appReviewConfig = isIosApp ? iosAppReview : isAndroidRieltorApp ? androidAppReview : null;
  const handleLikeClickAppStore = () => {
    // Open App Store link
    onLike();
    isIosApp && requestIosAppReview();
    isAndroidRieltorApp && requestAndroidAppReview();
  };

  const handleCloseDialog = () => {
    // Close dialog
    // You can use Redux or any state management to close the dialog
    onClose();
  };

  return (
      <Dialog
          className={classes.popupWrapper}
          open={true}
      >
        <DialogTitle style={{padding: '8px 16px'}}>
          <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
            <div>&nbsp;</div>

            <IconClose
                style={{cursor: 'pointer', color: rieltor.gray4}}
                onClick={handleCloseDialog}
            />
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typo variant={'title2'} mb={3} className={classes.title2}>
            {appReviewConfig?.reviewTitle || 'Будь ласка, оцініть наш додаток Rieltor від ЛУН в App Store'}
          </Typo>
        </DialogContent>

        <Box className={classes.stars}>
          {[1, 2, 3, 4, 5].map((star) => (
              <Star key={star} className={classes.star}/>
          ))}
        </Box>

        <ButtonPurple
            className={classes.starButton}
            onClick={handleLikeClickAppStore}
        >
          Оцінити
        </ButtonPurple>
      </Dialog>
  );
};

